"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Headset,
  Check,
  ClipboardCheck,
  Cpu,
  Gauge,
  Pencil,
  Loader,
  MessageSquare,
  Settings2,
  Rocket,
  Shield,
  Sparkles,
  Wand2,
  Wrench,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import ModelSelectorDialog from "./ModelSelectorDialog";
import SkillStoreSelector, { getUnboundIntegrationSkills } from "./SkillStoreSelector";
import assistantsBot from "../../assets/assistants-bot.png";
import { EXECUTION_MODE_OPTIONS } from "../../types/assistant.types";
import AssistantService from "../../services/assistant.service";

const STEPS = [
  { id: "identity", title: "Identity", subtitle: "Name and personality", icon: Sparkles },
  { id: "brain", title: "Brain", subtitle: "Model configuration", icon: Brain },
  { id: "capabilities", title: "Capabilities", subtitle: "Mode and controls", icon: Shield },
  { id: "review", title: "Review", subtitle: "Confirm and launch", icon: ClipboardCheck },
];

const PROGRESS_BY_STEP = ["w-1/4", "w-2/4", "w-3/4", "w-full"];

const PROVIDER_BRAND_LABELS = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  mistral: "Mistral",
  meta: "Meta",
  cohere: "Cohere",
  xai: "xAI",
  deepseek: "DeepSeek",
  openrouter: "OpenRouter",
};

function getModelDisplayName(name) {
  if (!name) return "-";
  return name;
}

function getProviderBrandLabel(model) {
  if (!model) return "Unknown";
  return model.brand || PROVIDER_BRAND_LABELS[(model.provider || "").toLowerCase()] || (model.provider || "Unknown");
}

const MODE_META = {
  reactive: {
    icon: Rocket,
    title: "None",
    description: "Responds directly to user input without additional reasoning steps.",
    color: "blue",
  },
  react: {
    icon: Wrench,
    title: "Simple",
    description: "Uses tools step by step to complete tasks.",
    color: "green",
  },
  multiagent: {
    icon: Brain,
    title: "Advanced",
    description: "Multi-agent orchestration. Coming soon.",
    color: "purple",
  },
};

function detectSkillProviderLabel(skill) {
  const explicit = String(
    skill?.provider || skill?.service || skill?.service_type || skill?.integration || skill?.category || ""
  ).toLowerCase();

  if (explicit.includes("gmail")) return "Gmail";
  if (explicit.includes("drive")) return "Drive";
  if (explicit.includes("calendar")) return "Calendar";

  const haystack = `${skill?.name || ""} ${skill?.friendly_name || ""} ${skill?.description || ""}`.toLowerCase();
  if (/(^|[_\s-])gmail|google\s*mail/.test(haystack)) return "Gmail";
  if (/(^|[_\s-])drive|google\s*drive/.test(haystack)) return "Drive";
  if (/(^|[_\s-])calendar|google\s*calendar/.test(haystack)) return "Calendar";

  return "Other";
}

function StepCard({ item, index, step, onSelect }) {
  const Icon = item.icon;
  const isActive = index === step;
  const isDone = index < step;

  return (
    <button
      type="button"
      onClick={() => {
        if (index <= step) onSelect(index);
      }}
      className={`text-left rounded-2xl border p-2.5 transition-all duration-300 ${
        isActive
          ? "bg-purple-600/20 border-purple-500/50"
          : isDone
          ? "bg-green-600/10 border-green-500/40"
          : "bg-white/5 border-white/10"
      } ${index <= step ? "hover:border-white/30" : "opacity-70 cursor-not-allowed"}`}
      disabled={index > step}
    >
      <div className="flex items-center gap-3">
        <span
          className={`inline-flex items-center justify-center w-8 h-8 rounded-xl ${
            isActive
              ? "bg-purple-500/30 text-purple-200"
              : isDone
              ? "bg-green-500/20 text-green-300"
              : "bg-black/30 text-gray-400"
          }`}
        >
          {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
        </span>
        <div>
          <p className="text-sm font-semibold text-white">{item.title}</p>
          <p className="text-xs text-gray-400">{item.subtitle}</p>
        </div>
      </div>
    </button>
  );
}

function SelectionCard({ selected, title, subtitle, onClick, badge, previewSrc, accentClass = "", variant = "compact" }) {
  const isAvatarVariant = variant === "avatar";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${isAvatarVariant ? "h-full min-h-[200px] flex flex-col p-4" : "min-h-[82px] p-3"} text-left rounded-2xl border transition-all duration-300 ${
        selected
          ? "bg-purple-600/20 border-purple-500/50 shadow-lg shadow-purple-500/10"
          : "bg-white/5 border-white/10 hover:border-white/30"
      }`}
    >
      {previewSrc && (
        <div className={`mb-3 relative overflow-hidden rounded-xl border border-white/20 bg-black/40 p-2 ${accentClass}`}>
          <div className="flex items-center gap-2">
            <img
              src={previewSrc}
              alt={title}
              className="h-12 w-12 shrink-0 rounded-full border border-white/25 object-cover"
            />
            <div className="min-w-0">
              <p className="truncate text-[11px] text-gray-400">Avatar identity</p>
            </div>
          </div>
        </div>
      )}
      <div className={`flex items-start justify-between gap-3 ${isAvatarVariant ? "min-h-[56px]" : ""}`}>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{title}</p>
          <p className={`mt-1 overflow-hidden text-xs leading-5 text-gray-400 ${isAvatarVariant ? "h-[60px]" : "line-clamp-1"}`}>
            {subtitle}
          </p>
        </div>
        {badge && (
          <span className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-1 rounded-full bg-white/10 text-gray-300">
            {badge}
          </span>
        )}
      </div>
    </button>
  );
}

export default function AssistantCreateSteps({
  submitting,
  onBack,
  onCreate,
  step: controlledStep,
  onStepChange,
  onCreateAvatar,
  name,
  setName,
  description,
  setDescription,
  avatarBase64,
  setAvatarBase64,
  avatarUrl,
  setAvatarUrl,
  selectedModel,
  setSelectedModel,
  selectedTemplate,
  setSelectedTemplate,
  llmModels,
  templates,
  projects = [],
  selectedProjectId,
  setSelectedProjectId,
  requireProjectSelection = false,
  accountId,
  executionMode,
  setExecutionMode,
  selectedSkills,
  setSelectedSkills,
  skillSettings,
  setSkillSettings,
  skillIntegrations,
  setSkillIntegrations,
  memoryEnabled,
  setMemoryEnabled,
  acceptsFilesAndImages,
  setAcceptsFilesAndImages,
  isDraft,
  setIsDraft,
  maxTokens,
  setMaxTokens,
  temperature,
  setTemperature,
  availableSkills,
  missingSkillIntegrations,
  checkingIntegrations,
  onGoToIntegrations,
  validationErrors,
  clearValidationError,
}) {
  const [internalStep, setInternalStep] = useState(0);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedAvatarTags, setSelectedAvatarTags] = useState([]);
  const [showAllAvatarTags, setShowAllAvatarTags] = useState(false);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const imageInputRef = useRef(null);
  const modelInitializedRef = useRef(false);

  const step = Number.isInteger(controlledStep) ? controlledStep : internalStep;
  const setStep = onStepChange || setInternalStep;

  const safeModels = Array.isArray(llmModels) && llmModels.length > 0
    ? llmModels
    : [{ id: "b7cad7e6-7369-4a1a-a3e1-1923615d6ccc", name: "GPT-4o Mini", provider: "openai" }];

  const recommendedModel = useMemo(() => {
    const exactRecommended = safeModels.find((model) =>
      /gpt\s*[-]?\s*4o\s*[-]?\s*mini/i.test(model?.name || "")
    );
    if (exactRecommended) return exactRecommended;

    const gptMini = safeModels.find((model) => /gpt/i.test(model?.name || "") && /mini/i.test(model?.name || ""));
    if (gptMini) return gptMini;

    return safeModels.find((model) => /gpt/i.test(model?.name || "")) || safeModels[0];
  }, [safeModels]);

  const orderedModels = useMemo(() => {
    if (!recommendedModel?.id) return safeModels;
    return [
      recommendedModel,
      ...safeModels.filter((model) => model.id !== recommendedModel.id),
    ];
  }, [recommendedModel, safeModels]);

  // Server-side avatar search. Empty term → the account list passed in via the
  // `templates` prop; a term → GET /assistant_templates/search (name match in
  // the database), debounced so typing doesn't fire a request per keystroke.
  const [searchResults, setSearchResults] = useState(null);
  useEffect(() => {
    const term = templateSearch.trim();
    if (!term) {
      setSearchResults(null);
      return;
    }
    if (!accountId) {
      setSearchResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const result = await AssistantService.searchTemplates({
          accountId,
          query: term,
          view: 'summary',
        });
        setSearchResults(result.success && Array.isArray(result.data) ? result.data : []);
      } catch (e) {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [templateSearch, accountId]);

  const baseTemplates = Array.isArray(templates) ? templates : [];
  const safeTemplates = searchResults !== null ? searchResults : baseTemplates;

  const selectedModelData = useMemo(
    () => safeModels.find((m) => m.id === selectedModel) || safeModels[0],
    [safeModels, selectedModel]
  );

  const selectedModelSupportsVision = useMemo(() => {
    const supportFlag =
      selectedModelData?.support_vision ??
      selectedModelData?.supportVision ??
      selectedModelData?.supportsVision;

    if (supportFlag === undefined || supportFlag === null) return true;
    return Boolean(supportFlag);
  }, [selectedModelData]);

  const selectedTemplateData = useMemo(() => {
    if (selectedTemplate === "00000000-0000-0000-0000-000000000000") {
      return { id: selectedTemplate, name: "No Avatar", description: "Use custom avatar only" };
    }
    return (
      safeTemplates.find((t) => t.id === selectedTemplate) ||
      safeTemplates[0] ||
      { id: "00000000-0000-0000-0000-000000000000", name: "No Avatar", description: "Use custom avatar only" }
    );
  }, [safeTemplates, selectedTemplate]);

  const availableAvatarTags = useMemo(() => {
    const tags = safeTemplates.flatMap((template) =>
      Array.isArray(template.tags) ? template.tags : []
    );
    return Array.from(new Set(tags)).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [safeTemplates]);

  const visibleAvatarTags = useMemo(() => {
    if (showAllAvatarTags) return availableAvatarTags;
    return availableAvatarTags.slice(0, 12);
  }, [availableAvatarTags, showAllAvatarTags]);

  // Name search runs server-side (see the searchResults effect); here we only
  // refine the loaded set by the selected tags.
  const filteredTemplates = useMemo(() => {
    return safeTemplates.filter((template) => {
      const templateTags = Array.isArray(template.tags) ? template.tags : [];
      return (
        selectedAvatarTags.length === 0 ||
        selectedAvatarTags.every((tag) => templateTags.includes(tag))
      );
    });
  }, [safeTemplates, selectedAvatarTags]);

  const toggleAvatarTag = (tag) => {
    setSelectedAvatarTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]
    );
  };

  const avatarTemplateCards = useMemo(() => {
    const accentClasses = [
      "before:absolute before:-right-6 before:-top-6 before:h-14 before:w-14 before:rounded-full before:bg-fuchsia-500/30 before:blur-xl",
      "before:absolute before:-left-6 before:bottom-0 before:h-14 before:w-14 before:rounded-full before:bg-cyan-500/25 before:blur-xl",
      "before:absolute before:right-2 before:top-1 before:h-10 before:w-10 before:rounded-full before:bg-violet-500/30 before:blur-lg",
      "before:absolute before:left-1 before:top-1 before:h-10 before:w-10 before:rounded-full before:bg-blue-500/25 before:blur-lg",
      "before:absolute before:right-4 before:bottom-1 before:h-10 before:w-10 before:rounded-full before:bg-purple-500/25 before:blur-lg",
    ];

    return filteredTemplates.map((template, index) => ({
      ...template,
      previewSrc: template.avatar || assistantsBot,
      accentClass: accentClasses[index % accentClasses.length],
    }));
  }, [filteredTemplates]);

  const selectedModeMeta = MODE_META[executionMode] || {
    icon: Rocket,
    title: executionMode,
    description: "Execution profile",
  };

  const hasMissingSkillIntegrations =
    Array.isArray(missingSkillIntegrations) && missingSkillIntegrations.length > 0;

  const missingIntegrationSummary = useMemo(() => {
    if (!hasMissingSkillIntegrations) return "";
    return missingSkillIntegrations.map((item) => item.label).join(", ");
  }, [hasMissingSkillIntegrations, missingSkillIntegrations]);

  // Selected skills that need a connected account but don't yet have a specific
  // integration picked from the dropdown. The owner must bind each one before
  // continuing, otherwise the agent ships with a skill it can't run.
  const unboundIntegrationSkills = useMemo(
    () => getUnboundIntegrationSkills(selectedSkills, availableSkills, skillIntegrations),
    [selectedSkills, availableSkills, skillIntegrations]
  );
  const hasUnboundIntegrationSkills = unboundIntegrationSkills.length > 0;
  const unboundIntegrationSummary = useMemo(
    () => unboundIntegrationSkills.map((s) => s.friendly_name || s.name).join(", "),
    [unboundIntegrationSkills]
  );

  const selectedSkillDetails = useMemo(() => {
    const safeAvailableSkills = Array.isArray(availableSkills) ? availableSkills : [];

    return (Array.isArray(selectedSkills) ? selectedSkills : []).map((skillName) => {
      const skill = safeAvailableSkills.find((item) => item.name === skillName);

      return {
        key: skillName,
        label: skill?.friendly_name || skillName,
        provider: detectSkillProviderLabel(skill || { name: skillName }),
      };
    });
  }, [availableSkills, selectedSkills]);

  const modelDisplayName = useMemo(() => getModelDisplayName(selectedModelData?.name), [selectedModelData]);

  const modelProviderLabel = useMemo(() => getProviderBrandLabel(selectedModelData), [selectedModelData]);

  useEffect(() => {
    const defaultModelId = recommendedModel?.id || safeModels[0]?.id;
    if (!defaultModelId) return;

    const hasValidSelection = safeModels.some((model) => model.id === selectedModel);

    if (!modelInitializedRef.current) {
      if (selectedModel !== defaultModelId) {
        setSelectedModel(defaultModelId);
      }
      modelInitializedRef.current = true;
      return;
    }

    if (!hasValidSelection) {
      setSelectedModel(defaultModelId);
    }
  }, [recommendedModel, safeModels, selectedModel, setSelectedModel]);

  useEffect(() => {
    if (!selectedModelSupportsVision && acceptsFilesAndImages) {
      setAcceptsFilesAndImages(false);
    }
  }, [selectedModelSupportsVision, acceptsFilesAndImages, setAcceptsFilesAndImages]);

  const canContinue = useMemo(() => {
    if (step === 0) return Boolean(name.trim()) && (!requireProjectSelection || Boolean(selectedProjectId));
    if (step === 2) {
      if (checkingIntegrations) return false;
      if (executionMode === "tool_using" && selectedSkills.length === 0) return false;
      if (hasMissingSkillIntegrations) return false;
      if (hasUnboundIntegrationSkills) return false;
    }
    return true;
  }, [step, name, requireProjectSelection, selectedProjectId, executionMode, selectedSkills, checkingIntegrations, hasMissingSkillIntegrations, hasUnboundIntegrationSkills]);

  const handleAvatarUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result;
      if (typeof base64 === "string") {
        setAvatarBase64(base64);
        setAvatarUrl("");
      }
    };
    reader.readAsDataURL(file);
  };

  const goNext = () => {
    if (!canContinue) return;
    setStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const goPrev = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const ModeIcon = selectedModeMeta.icon;

  return (
    <div className="flex h-full flex-col gap-4 p-4 md:p-5 overflow-hidden">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            className="flex items-center justify-center w-10 h-10 text-white hover:bg-white/10 rounded-xl transition"
            onClick={onBack}
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-white">Agent Creator</h2>
            <p className="text-sm text-gray-400">Build your agent loadout step by step</p>
          </div>
        </div>
        <div className="text-sm text-gray-400">
          Step {step + 1} of {STEPS.length}
        </div>
      </div>

      <div className="rounded-full h-2 bg-white/10 overflow-hidden">
        <div className={`h-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7] transition-all duration-500 ${PROGRESS_BY_STEP[step]}`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {STEPS.map((item, index) => (
          <StepCard key={item.id} item={item} index={index} step={step} onSelect={setStep} />
        ))}
      </div>

      <div className="rounded-2xl border border-white/20 bg-black/70 backdrop-blur-sm p-4 md:p-5 flex-1 min-h-0 flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 flex-1 min-h-0">
          <div className="md:col-span-4">
            <div className="rounded-2xl border border-white/15 bg-white/5 p-3 md:p-4 h-full max-h-[500px] md:max-h-none overflow-y-auto">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-3">Live Preview</p>

              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleAvatarUpload(e.target.files?.[0])}
                className="hidden"
              />

              <div className="relative w-full aspect-square max-w-[200px] md:max-w-[160px] xl:max-w-[200px] mx-auto rounded-xl border border-white/20 bg-black/40 overflow-hidden mb-3 md:mb-4">
                <img
                  src={avatarBase64 || avatarUrl || assistantsBot}
                  alt="Agent avatar"
                  className="w-full h-full object-cover"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={step !== 0}
                  className={`absolute top-2 right-2 h-8 px-2.5 border-white/20 bg-black/55 text-white text-xs hover:bg-black/75 ${
                    step !== 0 ? "cursor-not-allowed opacity-50 hover:bg-black/55" : ""
                  }`}
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />
                  Add Image
                </Button>
              </div>

              {step === 3 ? (
                <div className="space-y-3">
                  <div className="rounded-xl border border-white/15 bg-black/35 p-3">
                    <div className="mb-2 flex items-center gap-2 text-xs text-gray-400">
                      <Headset className="h-3.5 w-3.5 text-purple-300" />
                      Agent Snapshot
                    </div>
                    <p className="text-sm font-semibold text-white">{name?.trim() ? name : "Unnamed Agent"}</p>
                    <p className="mt-1 text-xs leading-5 text-gray-400">
                      {description?.trim() ? description : "No description provided."}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full border border-white/15 bg-black/35 px-2 py-1 text-[11px] text-gray-300">
                        {selectedModeMeta.title}
                      </span>
                      <span className="rounded-full border border-white/15 bg-black/35 px-2 py-1 text-[11px] text-gray-300">
                        {selectedSkillDetails.length} skills
                      </span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-white/15 bg-black/35 p-3">
                    <div className="mb-2 flex items-center gap-2 text-xs text-gray-400">
                      <MessageSquare className="h-3.5 w-3.5 text-purple-300" />
                      Response Preview
                    </div>
                    <div className="rounded-lg border border-white/10 bg-black/40 p-2 text-xs leading-5 text-gray-300">
                      Hi, I am {name?.trim() ? name : "your agent"}. I am ready to help using {selectedModeMeta.title.toLowerCase()} reasoning.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white mb-1">Agent Name</label>
                    {step === 0 ? (
                      <Input
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          clearValidationError("name");
                        }}
                        placeholder="e.g. Sales Copilot"
                        className={`w-full bg-black/60 border text-white placeholder-gray-500 ${
                          validationErrors.name ? "border-red-500" : "border-white/40"
                        }`}
                      />
                    ) : (
                      <div className="w-full min-h-[42px] rounded-md border border-white/15 bg-black/35 px-3 py-2 text-white/90">
                        {name?.trim() ? name : "-"}
                      </div>
                    )}
                    {validationErrors.name && (
                      <p className="text-red-400 text-xs mt-1">Agent name is required</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-white mb-1">Description</label>
                    {step === 0 ? (
                      <textarea
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="What should this agent help with?"
                        className="w-full rounded-md bg-black/60 border border-white/40 text-white placeholder-gray-500 px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                    ) : (
                      <div className="w-full min-h-[110px] rounded-md border border-white/15 bg-black/35 px-3 py-2 text-white/85 whitespace-pre-wrap break-words">
                        {description?.trim() ? description : "-"}
                      </div>
                    )}
                  </div>

                  {requireProjectSelection && (
                    <div>
                      <label className="block text-white mb-1">Project</label>
                      {step === 0 ? (
                        <>
                          <select
                            value={selectedProjectId || ""}
                            onChange={(e) => {
                              setSelectedProjectId?.(e.target.value);
                              clearValidationError("project");
                            }}
                            className={`w-full rounded-md bg-black/60 border text-white px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                              validationErrors.project ? "border-red-500" : "border-white/40"
                            }`}
                          >
                            <option value="">Select a project…</option>
                            {projects.map((p) => (
                              <option key={p.project_id} value={p.project_id}>
                                {p.name || "Untitled project"}
                              </option>
                            ))}
                          </select>
                          {validationErrors.project ? (
                            <p className="text-red-400 text-xs mt-1">Every agent must belong to a project</p>
                          ) : (
                            <p className="text-gray-500 text-xs mt-1">Defaults to the avatar's project — change it if needed.</p>
                          )}
                        </>
                      ) : (
                        <div className="w-full min-h-[42px] rounded-md border border-white/15 bg-black/35 px-3 py-2 text-white/90">
                          {projects.find((p) => p.project_id === selectedProjectId)?.name || "-"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-8 min-h-0">
            <div key={step} className="relative z-0 h-full overflow-y-auto pr-1 animate-slide-blur [animation-fill-mode:forwards]">
              {step === 0 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl text-white font-semibold">Identity</h3>
                    <p className="text-sm text-gray-400">Configure image, name and description from the preview panel.</p>
                  </div>

                  <div className="rounded-2xl border border-white/15 bg-gradient-to-br from-[#1A1033]/70 via-black/55 to-[#0D162A]/65 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Wand2 className="w-4 h-4 text-purple-300" />
                      <label className="text-white">Avatar</label>
                      <span className="text-xs text-gray-500">({safeTemplates.length})</span>
                    </div>

                    <div className="mb-3">
                      <Input
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        placeholder="Search avatar by name or description..."
                        className="w-full bg-black/60 border border-white/20 text-white placeholder-gray-500"
                      />
                    </div>

                    {safeTemplates.length === 0 && (
                      <div className="mb-3 rounded-xl border border-purple-400/40 bg-purple-600/10 p-3">
                        <p className="text-sm text-purple-100">You do not have avatars yet.</p>
                        <p className="mt-1 text-xs text-purple-200/80">
                          Create one and continue from this same agent step.
                        </p>
                        <Button
                          type="button"
                          onClick={() => onCreateAvatar?.()}
                          className="mt-3 h-8 bg-primary px-3 text-xs text-white hover:bg-violet-700"
                        >
                          Create New Avatar
                        </Button>
                      </div>
                    )}

                    {availableAvatarTags.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {visibleAvatarTags.map((tag) => {
                          const isActive = selectedAvatarTags.includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => toggleAvatarTag(tag)}
                              className={`rounded-full border px-3 py-1 text-xs transition-all duration-200 ${
                                isActive
                                  ? "border-purple-400/70 bg-purple-500/20 text-purple-200"
                                  : "border-white/20 bg-black/40 text-gray-300 hover:border-white/35"
                              }`}
                            >
                              {tag}
                            </button>
                          );
                        })}

                        {availableAvatarTags.length > 12 && (
                          <button
                            type="button"
                            onClick={() => setShowAllAvatarTags((prev) => !prev)}
                            className="rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs text-gray-300 hover:border-white/35"
                          >
                            {showAllAvatarTags
                              ? "Show less"
                              : `+${availableAvatarTags.length - 12} more`}
                          </button>
                        )}

                        {selectedAvatarTags.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedAvatarTags([])}
                            className="rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs text-gray-300 hover:border-white/35"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-1 max-h-[460px] overflow-y-auto">
                      <SelectionCard
                        selected={selectedTemplate === "00000000-0000-0000-0000-000000000000"}
                        title="No Avatar"
                        subtitle="Start from scratch with your own style."
                        badge={selectedTemplate === "00000000-0000-0000-0000-000000000000" ? "Selected" : "Custom"}
                        variant="avatar"
                        previewSrc={assistantsBot}
                        accentClass="before:absolute before:-right-4 before:top-1 before:h-12 before:w-12 before:rounded-full before:bg-indigo-500/25 before:blur-xl"
                        onClick={() => {
                          setSelectedTemplate("00000000-0000-0000-0000-000000000000");
                        }}
                      />

                      {avatarTemplateCards.map((template) => (
                        <SelectionCard
                          key={template.id}
                          selected={selectedTemplate === template.id}
                          title={template.name}
                          subtitle={template.description || "Avatar preset"}
                          badge={selectedTemplate === template.id ? "Selected" : "Avatar"}
                          variant="avatar"
                          previewSrc={template.previewSrc}
                          accentClass={template.accentClass}
                          onClick={() => {
                            setSelectedTemplate(template.id);
                          }}
                        />
                      ))}
                    </div>

                    {avatarTemplateCards.length === 0 && safeTemplates.length > 0 && (
                      <p className="text-xs text-gray-500 mt-2">No avatars match your search.</p>
                    )}
                  </div>

                </div>
              )}

              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl text-white font-semibold">Brain</h3>
                    <p className="text-sm text-gray-400">Choose the intelligence and base behavior profile.</p>
                  </div>

                  <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-purple-300" />
                        <label className="text-white">Model</label>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsModelSelectorOpen(true)}
                        className="h-8 border-white/20 bg-black/40 px-3 text-xs text-white hover:bg-black/60"
                      >
                        View all models
                      </Button>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsModelSelectorOpen(true)}
                      className="w-full rounded-xl border border-white/10 bg-black/40 p-3 text-left transition-all duration-200 hover:border-white/30"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{getModelDisplayName(selectedModelData?.name) || "Model"}</p>
                          <p className="mt-1 text-xs text-gray-400">
                            Provider: {getProviderBrandLabel(selectedModelData)}
                          </p>
                          {!selectedModelSupportsVision && (
                            <p
                              className="mt-1 text-[11px] text-amber-200/90"
                              title="This model does not support image or file inputs."
                            >
                              Vision not supported
                            </p>
                          )}
                        </div>

                        <span className="shrink-0 rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-wide text-gray-300">
                          {recommendedModel?.id === selectedModelData?.id ? "Recommended" : "Selected"}
                        </span>
                      </div>
                    </button>

                    <p className="mt-2 text-xs text-gray-500">{orderedModels.length} models available</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Cpu className="w-4 h-4 text-purple-300" />
                      <label className="text-white">Reasoning Strategy</label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {EXECUTION_MODE_OPTIONS.map((option) => {
                        const meta = MODE_META[option.value] || {
                          icon: Rocket,
                          title: option.label,
                          description: option.description || "Execution profile",
                          color: "purple",
                        };
                        const Icon = meta.icon;
                        const selected = executionMode === option.value;
                        const isDisabled = option.disabled;

                        const colorMap = {
                          blue: {
                            selected: "bg-blue-600/20 border-blue-500/50 shadow-lg shadow-blue-500/10",
                            icon: "text-blue-300",
                          },
                          green: {
                            selected: "bg-green-600/20 border-green-500/50 shadow-lg shadow-green-500/10",
                            icon: "text-green-300",
                          },
                          purple: {
                            selected: "bg-purple-600/20 border-purple-500/50 shadow-lg shadow-purple-500/10",
                            icon: "text-purple-300",
                          },
                        };
                        const colors = colorMap[meta.color] || colorMap.purple;

                        return (
                          <button
                            key={option.value}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => !isDisabled && setExecutionMode(option.value)}
                            className={`rounded-xl border p-3 text-left transition-all duration-300 ${
                              isDisabled
                                ? "opacity-40 cursor-not-allowed bg-white/5 border-white/10"
                                : selected
                                ? colors.selected
                                : "bg-white/5 border-white/10 hover:border-white/30"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className={`w-3.5 h-3.5 ${isDisabled ? "text-gray-500" : colors.icon}`} />
                              <p className="text-sm font-semibold text-white">{meta.title}</p>
                              {isDisabled && (
                                <span className="ml-auto text-xs text-gray-500">Soon</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 line-clamp-2">{meta.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <div className="flex items-center space-x-2 pt-2">
                    <Settings2 className="w-5 h-5 text-orange-400" />
                    <h3 className="text-white font-semibold text-lg">Advanced Settings</h3>
                  </div>

                  {/* Toggle para habilitar memoria */}
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-300">
                          <path d="M12 2a10 10 0 1 0 10 10H12V2z"></path>
                          <path d="M12 12 2.1 12.5"></path>
                          <path d="M12 12V2"></path>
                        </svg>
                        <div>
                          <label className="text-white text-sm font-medium">Memory</label>
                          <p className="text-xs text-gray-400 mt-0.5">Remember conversation context</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={memoryEnabled}
                          onChange={(e) => setMemoryEnabled(e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-700 rounded-full peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-purple-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                      </label>
                    </div>
                  </div>

                  {/* Toggle para habilitar archivos e imágenes */}
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-300">
                          <path d="M13.234 20.252 21 12.3"></path>
                          <path d="m16 6-8.414 8.586a2 2 0 0 0 0 2.828 2 2 0 0 0 2.828 0l8.414-8.586a4 4 0 0 0 0-5.656 4 4 0 0 0-5.656 0l-8.415 8.585a6 6 0 1 0 8.486 8.486"></path>
                        </svg>
                        <div>
                          <label className="text-white text-sm font-medium">Accept Files & Images</label>
                          <p className="text-xs text-gray-400 mt-0.5">Allow users to attach files and images in chat</p>
                        </div>
                      </div>
                      <label
                        className={`relative inline-flex items-center ${
                          selectedModelSupportsVision ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={acceptsFilesAndImages}
                          disabled={!selectedModelSupportsVision}
                          onChange={(e) => setAcceptsFilesAndImages(e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-700 rounded-full peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-purple-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                      </label>
                    </div>
                    {!selectedModelSupportsVision && (
                      <p className="mt-2 text-xs text-amber-200/90">
                        This model does not support vision. The setting is disabled.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl text-white font-semibold">Capabilities</h3>
                    <p className="text-sm text-gray-400">Tune behavior, control level, and limits.</p>
                  </div>

                  {hasMissingSkillIntegrations && (
                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3">
                      <p className="text-sm text-amber-100">
                        Missing integrations: {missingIntegrationSummary}
                      </p>
                      <p className="mt-1 text-xs text-amber-200/90">
                        Connect them to continue to the next step.
                      </p>
                      <Button
                        type="button"
                        onClick={onGoToIntegrations}
                        className="mt-3 h-8 bg-amber-500 px-3 text-xs text-black hover:bg-amber-400"
                      >
                        Connect Integrations
                      </Button>
                    </div>
                  )}

                  <div>
                    <label className="block text-white mb-2">Skills</label>
                    <SkillStoreSelector
                      skills={availableSkills}
                      selectedSkills={selectedSkills}
                      onChange={setSelectedSkills}
                      skillSettings={skillSettings || {}}
                      onSkillSettingsChange={(skillName, settings) =>
                        setSkillSettings({ ...(skillSettings || {}), [skillName]: settings })
                      }
                      skillIntegrations={skillIntegrations || {}}
                      onSkillIntegrationChange={(skillName, integrationId) =>
                        setSkillIntegrations({ ...(skillIntegrations || {}), [skillName]: integrationId })
                      }
                    />
                    {checkingIntegrations && (
                      <p className="text-gray-400 text-xs mt-2">Checking required integrations...</p>
                    )}

                    {executionMode === "tool_using" && selectedSkills.length === 0 && (
                      <p className="text-red-400 text-xs mt-2">Tool using mode requires at least one skill.</p>
                    )}

                    {hasUnboundIntegrationSkills && (
                      <p className="text-amber-300 text-xs mt-2">
                        Select an integration for: {unboundIntegrationSummary}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl text-white font-semibold">Review & Create</h3>
                    <p className="text-sm text-gray-400">Everything is ready. Confirm your agent loadout.</p>
                  </div>

                  <div className="rounded-2xl border border-white/15 bg-black/40 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <Settings2 className="h-4 w-4 text-purple-300" />
                      <p className="text-sm font-semibold text-white">Final Configuration</p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-gray-300">
                        <span className="block text-xs text-gray-500">Agent</span>
                        <p className="text-sm text-white">{name?.trim() || "-"}</p>
                        <p className="mt-1 line-clamp-2 text-xs text-gray-400">{description?.trim() || "No description provided."}</p>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-gray-300">
                        <span className="block text-xs text-gray-500">Avatar</span>
                        <p className="text-sm text-white">{selectedTemplateData?.name || "No Avatar"}</p>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-gray-300">
                        <span className="block text-xs text-gray-500">Model</span>
                        <p className="text-sm text-white">{modelDisplayName}</p>
                        <p className="mt-1 text-xs text-gray-400">{modelProviderLabel}</p>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-gray-300">
                        <span className="block text-xs text-gray-500">Reasoning Strategy</span>
                        <p className="text-sm text-white">{selectedModeMeta.title}</p>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-gray-300">
                        <span className="block text-xs text-gray-500">Memory</span>
                        <p className="text-sm">{memoryEnabled ? <span className="text-green-400">Enabled</span> : <span className="text-gray-400">Disabled</span>}</p>
                      </div>

                      <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-gray-300">
                        <span className="block text-xs text-gray-500">Accept Files & Images</span>
                        <p className="text-sm">{acceptsFilesAndImages ? <span className="text-green-400">Enabled</span> : <span className="text-gray-400">Disabled</span>}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/15 bg-black/40 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-purple-300" />
                      <p className="text-sm font-semibold text-white">Selected Skills</p>
                    </div>

                    {selectedSkillDetails.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedSkillDetails.map((skill) => (
                          <span
                            key={skill.key}
                            className="inline-flex items-center gap-1 rounded-full border border-purple-500/30 bg-purple-500/15 px-2 py-1 text-xs text-purple-200"
                          >
                            {skill.label}
                            <span className="rounded-full bg-black/30 px-1.5 py-0.5 text-[10px] text-gray-300">
                              {skill.provider}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">No skills selected.</p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-purple-500/30 bg-purple-500/10 p-4 flex items-start gap-3">
                    <ModeIcon className="w-5 h-5 text-purple-300 mt-0.5" />
                    <div>
                      <p className="text-sm text-white font-medium">{selectedModeMeta.title} profile is selected</p>
                      <p className="text-xs text-gray-300 mt-1">{selectedModeMeta.description}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative z-20 pt-4 mt-4 border-t border-white/10 flex items-center justify-between">
          <Button
            onClick={goPrev}
            disabled={step === 0 || submitting}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex flex-col items-end gap-1">
            {step === 2 && hasMissingSkillIntegrations && (
              <p className="text-xs text-amber-300">Connect integrations to enable Next.</p>
            )}
            {step === 2 && checkingIntegrations && (
              <p className="text-xs text-gray-400">Checking integrations...</p>
            )}

            {step < STEPS.length - 1 ? (
              <Button
                onClick={goNext}
                disabled={!canContinue || submitting}
                className="bg-primary hover:bg-violet-700 text-white min-w-[130px]"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={onCreate}
                disabled={submitting || !name.trim()}
                className="bg-primary hover:bg-violet-700 text-white min-w-[170px]"
              >
                {submitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Agent"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <ModelSelectorDialog
        open={isModelSelectorOpen}
        onOpenChange={setIsModelSelectorOpen}
        models={orderedModels}
        selectedModelId={selectedModel}
        recommendedModelId={recommendedModel?.id}
        onSelectModel={setSelectedModel}
      />
    </div>
  );
}
